<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'fixedAssets',
    path: '/fixed-assets',
    component: 'FixedAssetBrowser',
    title: () => gettext('Fixed Assets'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FixedAssetBrowser',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const selectedItems = ref([]);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'name',
      name: $fn.value.pgettext('FixedAssetBrowser', 'Name'),
      formatter: {
        name: 'routeLink',
        to: 'fixedAsset',
        params: {
          id: 'id',
        },
      },
    },
    {
      field: 'asset_type',
      name: $fn.value.pgettext('FixedAssetBrowser', 'Type'),
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'gettext',
            context: 'FixedAssetTypes',
            conversion: 'StartCase',
            filterInput: true,
          },
        ],
      },
    },
    {
      field: 'description',
      name: $fn.value.pgettext('FixedAssetBrowser', 'Description'),
    },
    {
      field: 'inventory_no',
      name: $fn.value.pgettext('FixedAssetBrowser', 'Inventory Number'),
    },
    {
      field: 'sku',
      name: $fn.value.pgettext('FixedAssetBrowser', 'SKU'),
    },
    {
      field: 'quantity',
      name: $fn.value.pgettext('FixedAssetBrowser', 'Quantity'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'acquisition_date',
      name: $fn.value.pgettext('FixedAssetBrowser', 'Acquisition Date'),
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = () => {
    return $fn.value.gridDataSource('integro_api/list_fixed_asset');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedItems,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.gettext('Fixed Assets'),
      icon: $config.value.fixedAsset.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_vm4cu7x90_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_0z6jd4rqm_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4615685607825208_showModal_y27lc0fah_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'MCreateFixedAsset',
    };
  };

  const act_7315092382398562_closeModal_vm4cu7x90 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_vm4cu7x90_getActionArgs,
    });
  const inst_evh_7315092382398562_close_2m1ai7itc =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_vm4cu7x90,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_2m1ai7itc],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_schxpl90u =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_6slthz2zt =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_schxpl90u,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_6slthz2zt],
    event: 'reload',
  });
  const act_2248226175642056_setData_0z6jd4rqm = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_0z6jd4rqm_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_0sfofe9it =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_0z6jd4rqm,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_0sfofe9it],
      event: 'selectedRowsChanged',
    });
  const act_4615685607825208_showModal_y27lc0fah =
    new actionTypes.ShowModalAction({
      actionArgs: act_4615685607825208_showModal_y27lc0fah_getActionArgs,
    });
  const inst_evh_4615685607825208_click_b6tt32ibn =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_showModal_y27lc0fah,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_4615685607825208_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4615685607825208_click_b6tt32ibn],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_2m1ai7itc,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_6slthz2zt,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_0sfofe9it,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_4615685607825208_click_b6tt32ibn,
    inst_evh_4615685607825208_click,
  };

  const evh_7315092382398562_close_2m1ai7itc = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_2m1ai7itc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_6slthz2zt = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_6slthz2zt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_0sfofe9it = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_0sfofe9it.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click_b6tt32ibn = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click_b6tt32ibn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="FixedAssetBrowser"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpDataGrid
      name="fixedAssets"
      :headerTitle="gettext('Fixed assets')"
      :items="ds"
      checkbox-row-selection
      footer-totals
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2">
          <ItpButton
            :icon="$config.fixedAsset.createFixedAssetIcon"
            :text="pgettext('FixedAssets', 'Create fixed asset...')"
            size="sm"
            variant="light"
            @click="evh_4615685607825208_click($event, {})"
          >
          </ItpButton>
          <PageHelp path="/assets/fixed-assets.html"> </PageHelp>
        </div>
      </template>
    </ItpDataGrid>
  </ItpPage>
</template>
